import { useEffect } from 'react';

interface PageMetaProps {
  title: string;
  description: string;
  keywords?: string;
  canonicalUrl?: string;
  ogTitle?: string;
  ogDescription?: string;
  ogType?: string;
  ogUrl?: string;
  twitterCard?: string;
  schemaObjects?: Record<string, unknown>[];
}

export default function PageMeta({
  title,
  description,
  keywords,
  canonicalUrl,
  ogTitle,
  ogDescription,
  ogType = 'website',
  ogUrl,
  twitterCard = 'summary_large_image',
  schemaObjects = [],
}: PageMetaProps) {
  const schemaKey = JSON.stringify(schemaObjects);

  useEffect(() => {
    document.title = title;

    const setMetaTag = (name: string, content: string, isProperty = false) => {
      const attr = isProperty ? 'property' : 'name';
      let el = document.querySelector(`meta[${attr}="${name}"]`) as HTMLMetaElement | null;
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute(attr, name);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };

    setMetaTag('description', description);
    if (keywords) setMetaTag('keywords', keywords);

    if (canonicalUrl) {
      let link = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
      if (!link) {
        link = document.createElement('link');
        link.setAttribute('rel', 'canonical');
        document.head.appendChild(link);
      }
      link.setAttribute('href', canonicalUrl);
    }

    if (ogTitle) setMetaTag('og:title', ogTitle, true);
    if (ogDescription) setMetaTag('og:description', ogDescription, true);
    setMetaTag('og:type', ogType, true);
    if (ogUrl) setMetaTag('og:url', ogUrl, true);

    setMetaTag('twitter:card', twitterCard);
    if (ogTitle) setMetaTag('twitter:title', ogTitle);
    if (ogDescription) setMetaTag('twitter:description', ogDescription);

    const scriptIds: string[] = [];
    schemaObjects.forEach((schema, index) => {
      const scriptId = `schema-jsonld-${index}`;
      let el = document.getElementById(scriptId) as HTMLScriptElement | null;
      if (!el) {
        el = document.createElement('script');
        el.type = 'application/ld+json';
        el.id = scriptId;
        document.head.appendChild(el);
      }
      el.textContent = JSON.stringify(schema);
      scriptIds.push(scriptId);
    });

    return () => {
      scriptIds.forEach((id) => {
        const el = document.getElementById(id);
        if (el) el.remove();
      });
    };
  }, [title, description, keywords, canonicalUrl, ogTitle, ogDescription, ogType, ogUrl, twitterCard, schemaKey]);

  return null;
}